import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Maps,
  MapsComponent,
  MapsTooltip,
  Selection,
  Highlight,
  MapsModule,
  MapsTooltipService,
  DataLabelService,
} from '@syncfusion/ej2-angular-maps';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { italyMap } from '../../../../../../shared/italy-regions';
import { NtPotentialRegionInterface } from '../../../../../../shared/interfaces/nt-potential-region.interface';

Maps.Inject(MapsTooltip, Selection, Highlight);

@Component({
  selector: 'app-merged-heatmap',
  imports: [MapsModule, CommonModule],
  providers: [MapsTooltipService, DataLabelService],
  templateUrl: './merged-heatmap.component.html',
  styleUrl: './merged-heatmap.component.css',
})
export class MergedHeatmapComponent implements OnInit, OnChanges {
  @ViewChild(MapsComponent) public maps?: MapsComponent;

  @Input() heatmapColorStatus: 'Red' | 'White' = 'White';
  @Input() dataMerged: NtPotentialRegionInterface[] = [];
  @Output() closeModal = new EventEmitter<boolean>();

  public mapWidth: string = '98%';
  public mapHeight: string = '98%';

  public medicineId = 0;
  public countryId = 1;
  public companyId = 0;

  public layers?: object[];

  public isLoading: boolean = true;

  constructor(private dataService: DataService) {}

  ngOnInit(): void {
    this.layers = [{ shapeData: italyMap }];

    this.medicineId = sessionStorage.getItem('medicineId')
      ? Number(sessionStorage.getItem('medicineId'))
      : 0;
    this.countryId = sessionStorage.getItem('countryId')
      ? Number(sessionStorage.getItem('countryId'))
      : 1;
    this.companyId = sessionStorage.getItem('companyId')
      ? Number(sessionStorage.getItem('companyId'))
      : 0;

    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['heatmapColorStatus'] &&
      !changes['heatmapColorStatus'].firstChange
    ) {
      this.loadData();
    }
  }

  loadData() {
    this.isLoading = true;

    this.dataService
      .getHeatmapinfoPossInEconomia(
        this.companyId,
        this.medicineId,
        this.countryId,
      )
      .subscribe((data: NtPotentialRegionInterface[]) => {
        if (data && data.length > 0) {
          const regioniHeatmapData = data.map((item) => {
            let percentage =
              item.percentage !== undefined
                ? parseFloat(item.percentage).toFixed(2)
                : 0;

            const tempPercentage =
              typeof percentage === 'string' ? parseFloat(percentage) : percentage;
            if (tempPercentage >= 100.0) {
              percentage = '100.00';
            }

            return {
              ...item,
              tooltipText: `${
                item.regionName === 'Prov. Aut. di Bolzano'
                  ? 'PA di Bolzano'
                  : item.regionName === 'Prov. Aut. di Trento'
                    ? 'PA di Trento'
                    : item.regionName
              }: <br> ${percentage}%`,
              percentage,
              color: '',
            };
          });

          this.layers = [
            {
              shapeData: italyMap,
              shapePropertyPath: 'id',
              shapeDataPath: 'regionId',
              dataSource: regioniHeatmapData,
              tooltipSettings: {
                visible: true,
                valuePath: 'tooltipText',
                textStyle: { size: '10px' },
              },
              highlightSettings: { enable: false, fill: '#00CEBC' },
              selectionSettings: {
                enable: false,
                fill: '',
                opacity: 1,
                border: {
                  color:
                    this.heatmapColorStatus === 'White' ? '#162456' : '#FFFFFF',
                  width: this.heatmapColorStatus === 'White' ? 0.7 : 0.4,
                },
              },
              shapeSettings: {
                border: {
                  color:
                    this.heatmapColorStatus === 'White' ? '#162456' : '#FFFFFF',
                  width: this.heatmapColorStatus === 'White' ? 0.7 : 0.4,
                },
                borderWidth: this.heatmapColorStatus === 'White' ? 0.7 : 4,
                colorValuePath: 'percentage',
                colorMapping: this.getColorMapping(),
              },
            },
          ];

          setTimeout(() => {
            if (this.maps) {
              this.maps.refresh();
            }

            setTimeout(() => {
              this.isLoading = false;
            }, 1000);
          }, 50);
        } else {
          this.isLoading = false;
        }
      });
  }

  onCloseModal(value: boolean) {
    this.closeModal.emit(value);
  }

  private getColorMapping(): any[] {
    if (this.heatmapColorStatus === 'Red') {
      return [
        { from: 0, to: 0, color: '#c10007' },
        { from: 0.01, to: 8.33, color: '#dd3a3f' },
        { from: 8.34, to: 16.66, color: '#e95b43' },
        { from: 16.67, to: 24.99, color: '#f47938' },
        { from: 25.0, to: 33.32, color: '#fba63a' },
        { from: 33.33, to: 41.65, color: '#ffd04c' },
        { from: 41.66, to: 49.98, color: '#ffe84a' },
        { from: 49.99, to: 58.31, color: '#e4f042' },
        { from: 58.32, to: 66.64, color: '#c5e73c' },
        { from: 66.65, to: 74.97, color: '#9fdd38' },
        { from: 74.98, to: 83.3, color: '#7cca33' },
        { from: 83.31, to: 91.63, color: '#51b42f' },
        { from: 91.64, to: 94.99, color: '#2e9c2b' },
        { from: 95.0, to: 98.99, color: '#27982d' },
        { from: 99.0, to: 100, color: '#008236' },
      ];
    }

    return [
      { from: 0, to: 0, color: '#ffffff' },
      { from: 0.01, to: 8.33, color: '#f8f8f8' },
      { from: 8.34, to: 16.66, color: '#eaeaea' },
      { from: 16.67, to: 24.99, color: '#e0e0e0' },
      { from: 25.0, to: 33.32, color: '#d0d0b3' },
      { from: 33.33, to: 41.65, color: '#d0d09c' },
      { from: 41.66, to: 49.98, color: '#c4c44d' },
      { from: 49.99, to: 58.31, color: '#9fbf4d' },
      { from: 58.32, to: 66.64, color: '#7fb34d' },
      { from: 66.65, to: 74.97, color: '#60a34d' },
      { from: 74.98, to: 83.3, color: '#449E51' },
      { from: 83.31, to: 91.63, color: '#3C9841' },
      { from: 91.64, to: 94.99, color: '#43a047' },
      { from: 95.0, to: 98.99, color: '#399c44' },
      { from: 99.0, to: 100, color: '#008236' },
    ];
  }

  getGradientBackground(): string {
    if (this.heatmapColorStatus === 'Red') {
      return 'linear-gradient(90deg, #c10007 0%, #dd3a3f 8.33%, #e95b43 16.66%, #f47938 24.99%, #fba63a 33.32%, #ffd04c 41.65%, #ffe84a 49.98%, #e4f042 58.31%, #c5e73c 66.64%, #9fdd38 74.97%, #7cca33 83.30%, #51b42f 91.63%, #2e9c2b 99.99%, #008236 100%)';
    }

    return 'linear-gradient(90deg, #ffffff 0%, #f8f8f8 8.33%, #eaeaea 16.66%, #e0e0e0 24.99%, #d0d0b3 33.32%, #d0d09c 41.65%, #c4c44d 49.98%, #9fbf4d 58.31%, #7fb34d 66.64%, #60a34d 74.97%, #40934d 83.30%, #2e7d32 91.63%, #1b5e20 99.99%, #008236 100%)';
  }
}